import type { SupplierVendorNexAliasGroup } from '../hooks/useGetSettings';
import { normalizeInvoiceVendorAliasKey } from './invoiceVendorDisplay';

export type MergeVendorNexAliasMappingsResult =
    | { ok: true; mappings: SupplierVendorNexAliasGroup[] }
    | { ok: false; reason: 'no_supplier_group' };

export interface EnsureSupplierVendorNexAliasGroup {
    supplierId: string;
    supplierTitle: string;
    nexVendorId?: number;
}

/**
 * Append invoice text aliases and a NEX vendor label onto an existing supplier mapping group,
 * or create a new group when `ensureGroup` is provided.
 *
 * When `supplierId` is known, only that supplier's group is updated (or created via
 * `ensureGroup`). Another supplier that happens to use the same Nexsyis name is left alone.
 * When `supplierId` is absent, fall back to the group whose saved NEX label matches.
 */
export function mergeInvoiceAliasesIntoVendorNexAliasMappings(params: {
    mappings: SupplierVendorNexAliasGroup[];
    supplierId?: string | null;
    nexVendorName: string;
    invoiceAliasTexts: string[];
    ensureGroup?: EnsureSupplierVendorNexAliasGroup;
}): MergeVendorNexAliasMappingsResult {
    const nex = params.nexVendorName.trim();
    if (!nex) return { ok: false, reason: 'no_supplier_group' };

    const norm = normalizeInvoiceVendorAliasKey;
    const mappings = [...params.mappings];
    const supplierId = (params.supplierId ?? '').trim();

    let groupIndex = supplierId ? mappings.findIndex((g) => g.supplierId === supplierId) : -1;
    if (groupIndex < 0 && !supplierId) {
        groupIndex = mappings.findIndex((g) =>
            g.nexVendorMappings.some((m) => norm(m.vendorName) === norm(nex))
        );
    }
    if (groupIndex < 0 && params.ensureGroup) {
        const eg = params.ensureGroup;
        const sid = eg.supplierId.trim();
        const title = eg.supplierTitle.trim();
        if (!sid || !title) return { ok: false, reason: 'no_supplier_group' };
        mappings.push({
            supplierId: sid,
            supplierTitle: title,
            invoiceTextAliases: [],
            nexVendorMappings: [
                eg.nexVendorId != null && Number.isFinite(eg.nexVendorId)
                    ? { vendorId: eg.nexVendorId, vendorName: nex }
                    : { vendorName: nex },
            ],
        });
        groupIndex = mappings.length - 1;
    }
    if (groupIndex < 0) {
        return { ok: false, reason: 'no_supplier_group' };
    }

    const group = { ...mappings[groupIndex] };
    const mergedAliases = [...group.invoiceTextAliases];
    for (const a of params.invoiceAliasTexts) {
        const text = a.trim();
        if (!text) continue;
        if (!mergedAliases.some((existing) => norm(existing) === norm(text))) {
            mergedAliases.push(text);
        }
    }
    group.invoiceTextAliases = mergedAliases;

    const nexByKey = new Map(
        group.nexVendorMappings.map((m) => [m.vendorName.toLowerCase(), m] as const)
    );
    if (!nexByKey.has(nex.toLowerCase())) {
        const ensureVendorId = params.ensureGroup?.nexVendorId;
        nexByKey.set(
            nex.toLowerCase(),
            ensureVendorId != null && Number.isFinite(ensureVendorId)
                ? { vendorId: ensureVendorId, vendorName: nex }
                : { vendorName: nex }
        );
    }
    group.nexVendorMappings = Array.from(nexByKey.values());
    mappings[groupIndex] = group;

    return { ok: true, mappings };
}
